import { useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { apiGet, viewReceipt } from "@/lib/api";
import PaymentInvoice from "@/components/PaymentInvoice";
import { FileText } from "lucide-react";

function fmtDate(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  return d.toLocaleDateString("en-MY", { day: "2-digit", month: "short", year: "numeric" });
}

function fmtMonth(m) {
  if (!m) return "—";
  const [yr, mo] = m.split("-");
  const labels = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
  return `${labels[parseInt(mo,10)-1]} ${yr}`;
}

export default function MyPayments() {
  const { user } = useAuth();
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [invoicePayment, setInvoicePayment] = useState(null);

  useEffect(() => {
    apiGet("/payments").then(r => setPayments(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, []);

  return (
    <div>
      <div className="section-panel" data-testid="my-payments-panel">
        <div className="section-head">
          <div className="section-title">My Payment History</div>
          <span className="sec-action" onClick={() => alert("Export coming soon!")} data-testid="export-pdf-btn">Export PDF</span>
        </div>

        {loading ? (
          <div style={{ padding: 24, textAlign: "center", color: "var(--text2)", fontSize: 12 }}>Loading payments...</div>
        ) : payments.length === 0 ? (
          <div style={{ textAlign: "center", color: "var(--text2)", padding: 24, fontSize: 12 }}>
            No payments found. <a href="/submit-payment" style={{ color: "var(--brand)" }}>Submit your first payment</a>
          </div>
        ) : (
          <>
            {/* Desktop / tablet table */}
            <div className="payments-table-wrap" style={{ overflowX: "auto" }}>
              <table className="percasa-table">
                <thead>
                  <tr><th>Month</th><th>Category</th><th>Amount</th><th>Submitted</th><th>Status</th><th>Ref / Remarks</th><th>Actions</th></tr>
                </thead>
                <tbody>
                  {payments.map(p => (
                    <tr key={p.id}>
                      <td><span className="mono">{fmtMonth(p.month)}</span></td>
                      <td style={{ fontSize: 11 }}>{p.category}</td>
                      <td><span className="mono">RM {p.amount?.toFixed(2)}</span></td>
                      <td style={{ fontSize: 10, color: "var(--text2)" }}>{fmtDate(p.submitted_at)}</td>
                      <td><span className={`badge ${p.status}`}>{capitalize(p.status)}</span></td>
                      <td style={{ fontSize: 10, color: "var(--text2)" }}>
                        {p.billcode && <div className="mono" style={{ color: "var(--brand-dark)" }}>Bill: {p.billcode}</div>}
                        {p.status === "rejected" && p.reject_reason
                          ? <span style={{ color: "var(--danger)" }}>{p.reject_reason}</span>
                          : (p.remarks || (p.billcode ? "" : "—"))}
                      </td>
                      <td>
                        <div style={{ display: "flex", gap: 4, flexWrap: "wrap" }}>
                          {p.receipt_id && (
                            <button
                              type="button"
                              className="btn"
                              style={{ fontSize: 10, padding: "4px 8px" }}
                              onClick={() => viewReceipt(p.receipt_id)}
                              data-testid={`view-receipt-${p.id}`}
                            >
                              Receipt
                            </button>
                          )}
                          {p.status === "approved" ? (
                            <button
                              type="button"
                              className="btn"
                              style={{ fontSize: 10, padding: "4px 8px", gap: 4, display: "inline-flex", alignItems: "center" }}
                              onClick={() => setInvoicePayment(p)}
                              data-testid={`view-invoice-${p.id}`}
                            >
                              <FileText size={12} /> Invoice
                            </button>
                          ) : !p.receipt_id ? (
                            <span style={{ fontSize: 10, color: "var(--text2)" }}>—</span>
                          ) : null}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Mobile card list */}
            <div className="payments-card-list" data-testid="payments-card-list">
              {payments.map(p => (
                <div key={p.id} className="payment-card">
                  <div className="payment-card-top">
                    <span className="mono" style={{ fontWeight: 600 }}>{fmtMonth(p.month)}</span>
                    <span className={`badge ${p.status}`}>{capitalize(p.status)}</span>
                  </div>
                  <div className="payment-card-row">
                    <span>{p.category}</span>
                    <span className="mono" style={{ fontWeight: 600 }}>RM {p.amount?.toFixed(2)}</span>
                  </div>
                  <div className="payment-card-meta">
                    <span>Submitted {fmtDate(p.submitted_at)}</span>
                    {p.billcode && <span className="mono">Bill: {p.billcode}</span>}
                  </div>
                  {p.status === "rejected" && p.reject_reason && (
                    <div className="payment-card-meta" style={{ color: "var(--danger)" }}>{p.reject_reason}</div>
                  )}
                  {p.remarks && (
                    <div className="payment-card-meta">{p.remarks}</div>
                  )}
                  <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginTop: 8 }}>
                    {p.receipt_id && (
                      <button
                        type="button"
                        className="btn"
                        style={{ fontSize: 11, padding: "6px 10px" }}
                        onClick={() => viewReceipt(p.receipt_id)}
                      >
                        Receipt
                      </button>
                    )}
                    {p.status === "approved" && (
                      <button
                        type="button"
                        className="btn"
                        style={{ fontSize: 11, padding: "6px 10px", gap: 4, display: "inline-flex", alignItems: "center" }}
                        onClick={() => setInvoicePayment(p)}
                      >
                        <FileText size={12} /> Invoice
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
      </div>

      {invoicePayment && (
        <PaymentInvoice
          payment={invoicePayment}
          resident={user}
          onClose={() => setInvoicePayment(null)}
        />
      )}
    </div>
  );
}

function capitalize(s) { return s ? s.charAt(0).toUpperCase() + s.slice(1) : ""; }
